import React, { useEffect, useMemo, useState } from 'react';
import Joi from 'joi';
import { Drawer, Button } from '@fashionnovateam/nova-ui-toolkit';
import { omit } from 'lodash';
import toast from '../../Toast/Toast';
import { PurchaseOrder } from '../../../Lib/Types/purchase-order';
import { useCreatePurchaseOrderRevision } from '../../../Lib/Hooks/purchase-orders.hooks';
import {
  FormErrors,
  PurchaseOrderRevisionForm,
  PurchaseOrderRevisionFormSchema,
} from './validation';
import { PoLevelForm } from './PoLevelForm';
import { ItemsLevelForm } from './ItemsLevelForm';
import { getFormIdFromError } from './formTargetId';
import {
  PurchaseOrderRevisionStatus,
  PurchaseOrderRevisionUpdates,
} from '../../../Lib/Types/purchase-order-revision';
import { ReasonForRequest } from './ReasonForRequest';

type PurchaseOrderDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  purchaseOrder: PurchaseOrder;
  userId: string;
};

const cleanForm = (form: PurchaseOrderRevisionForm) =>
  form.due_date
    ? {
        ...form,
        due_date: omit(form.due_date, 'vendor_acknowledged'),
      }
    : form;

export function PurchaseOrderRevisionDrawer({
  isOpen,
  onClose,
  purchaseOrder,
  userId,
}: PurchaseOrderDrawerProps) {
  const { mutateAsync: createPurchaseOrderRevision, isLoading } =
    useCreatePurchaseOrderRevision();
  const [showErrors, setShowErrors] = useState(false);

  const isFca = purchaseOrder.incoterm === 'FCA';

  const [form, setForm] = useState<PurchaseOrderRevisionForm>({});
  const fullErrors = useMemo(
    () =>
      (
        PurchaseOrderRevisionFormSchema(isFca).validate(form, {
          abortEarly: false,
        }).error?.details as Joi.ValidationErrorItem[] | undefined
      )?.reduce(
        (acc, cur) => ({
          ...acc,
          [cur.path.join('.')]: cur.message,
        }),
        {} as FormErrors,
      ),
    [isFca, form],
  );
  const errors = showErrors ? fullErrors : undefined;

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (fullErrors) {
      setShowErrors(true);
      return;
    }

    createPurchaseOrderRevision({
      userId,
      purchaseOrderId: purchaseOrder.po_number,
      updates: cleanForm(form) as PurchaseOrderRevisionUpdates,
    })
      .then(() => {
        toast({ title: 'Revision created successfully' }, { type: 'success' });
        onClose();
      })
      .catch((err) => {
        console.error('Failed to create revision:', err);
        toast(
          { title: 'Failed to create revision', text: err.toString() },
          { type: 'error' },
        );
      });
  };

  useEffect(() => {
    if (!errors) return;
    const { id, index } = getFormIdFromError(errors);
    if (id) {
      const targetId = index !== null ? `${id}-${index}` : id;
      const target = document.getElementById(targetId);
      if (target) {
        target.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [showErrors]);

  useEffect(() => {
    if (
      !isOpen &&
      purchaseOrder.vendor_revision &&
      [
        PurchaseOrderRevisionStatus.REVISION_PENDING_PD,
        PurchaseOrderRevisionStatus.REVISION_PD_IN_NEGOTIATION,
        PurchaseOrderRevisionStatus.REVISION_REQUESTED,
        PurchaseOrderRevisionStatus.REVISION_NEGOTIATION,
      ].includes(purchaseOrder.vendor_revision.status)
    ) {
      const formValues = {
        ...purchaseOrder.vendor_revision.updates,
        due_date: purchaseOrder.vendor_revision.updates.due_date && {
          ...purchaseOrder.vendor_revision.updates.due_date,
          vendor_acknowledged:
            new Date(purchaseOrder.vendor_revision.updates.due_date.new_value) >
            new Date(purchaseOrder.original_due_date || purchaseOrder.due_date)
              ? true
              : undefined,
        },
      };
      setForm(formValues);
    }
  }, [isOpen, isFca, purchaseOrder.vendor_revision]);

  return (
    <Drawer
      noMargin
      width="600px"
      isOpen={isOpen}
      noBackdrop
      className="h-full"
      onClose={onClose}
    >
      <Drawer.Content>
        <form
          onSubmit={handleSubmit}
          noValidate
          className="flex flex-col flex-grow"
        >
          <div className="flex flex-col gap-5 p-3">
            <div className="flex flex-col gap-2">
              <p className="text-2xl font-medium text-base-primary">
                Request Changes
              </p>
              <p className="text-xsm font-normal text-base-disabled">
                Please provide all of your request changes. Our team will email
                you with an approval or response.
              </p>
            </div>
            <ReasonForRequest
              reasonForRequest={form.reason_for_request || ''}
              updateForm={setForm}
              errors={errors}
              disabled={isLoading}
            />
            <PoLevelForm
              purchaseOrder={purchaseOrder}
              form={form}
              updateForm={setForm}
              disabled={isLoading}
              errors={errors}
            />
            <ItemsLevelForm
              purchaseOrder={purchaseOrder}
              form={form}
              updateForm={setForm}
              disabled={isLoading}
              errors={errors}
            />
            {!fullErrors?.[''] && (
              <Button
                type="submit"
                disabled={isLoading || !!errors}
                loading={isLoading}
                className="w-full"
                color="primary"
              >
                {purchaseOrder.vendor_revision?.status ===
                  PurchaseOrderRevisionStatus.REVISION_REQUESTED ||
                purchaseOrder.vendor_revision?.status ===
                  PurchaseOrderRevisionStatus.REVISION_NEGOTIATION
                  ? 'Update Request'
                  : 'Submit Request'}
              </Button>
            )}
          </div>
        </form>
      </Drawer.Content>
    </Drawer>
  );
}
